import { derived, writable, type Readable } from 'svelte/store';
import type Clerk from '@clerk/clerk-js';
import { loadClerk } from './clerkClient';
import { attachConvexAuth } from './convexClient';
import { clearLiveQueryCache } from './convexRealtime';

export interface AuthState {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null;
  email: string | null;
  roles: Set<string>;
}

const authState = writable<AuthState>({
  isLoaded: false,
  isSignedIn: false,
  userId: null,
  email: null,
  roles: new Set()
});

let clerkInstance: Clerk | null = null;
let removeListener: (() => void) | null = null;
let convexTokenFetcher: (() => Promise<string | null>) | null = null;
let convexAuthSignedIn: boolean | null = null;

function extractRoles(clerk: Clerk): Set<string> {
  const roles = new Set<string>();
  const user = clerk.user as any;
  if (!user) return roles;

  const candidateArrays: unknown[] = [
    user?.publicMetadata?.roles,
    user?.privateMetadata?.roles
  ];

  for (const value of candidateArrays) {
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (typeof entry === 'string' && entry.trim()) {
          roles.add(entry.trim());
        }
      }
    }
  }

  if (user?.publicMetadata?.admin === true || user?.privateMetadata?.admin === true) {
    roles.add('admin');
  }

  return roles;
}

function pushState(clerk: Clerk) {
  const user = clerk.user;
  const raw = clerk as any;
  const isSignedIn = Boolean(raw?.isSignedIn ?? clerk.session);

  // Re-trigger the Convex token fetch whenever the sign-in state flips.
  // setAuth fetches immediately, and the fetcher returns null once the Clerk
  // session is gone — without this, the websocket keeps the old identity
  // (and live queries keep serving that user's data) until the token expires.
  if (convexTokenFetcher && isSignedIn !== convexAuthSignedIn) {
    convexAuthSignedIn = isSignedIn;
    attachConvexAuth(convexTokenFetcher);
    // The signed-in identity changed (sign-in or sign-out). Seeded live-query
    // data is standing/redaction-sensitive, so drop it to avoid ever seeding a
    // re-mounted page with the previous identity's data.
    clearLiveQueryCache();
  }

  authState.set({
    isLoaded: true,
    isSignedIn,
    userId: user?.id ?? null,
    email: user?.primaryEmailAddress?.emailAddress ?? null,
    roles: extractRoles(clerk)
  });
}

export async function initializeAuth(): Promise<Clerk> {
  const clerk = await loadClerk();
  clerkInstance = clerk;

  convexTokenFetcher = async () => {
    const session = clerk.session;
    if (!session) return null;
    try {
      return await session.getToken({ template: 'convex' });
    } catch (error) {
      console.error('Failed to fetch Convex token from Clerk session', error);
      return null;
    }
  };
  // The initial pushState below attaches the fetcher via the transition check.
  convexAuthSignedIn = null;

  if (removeListener) {
    removeListener();
    removeListener = null;
  }

  const listener = () => pushState(clerk);
  // addListener returns an unsubscribe function
  removeListener = clerk.addListener(listener);
  pushState(clerk);

  return clerk;
}

export function getAuthState(): Readable<AuthState> {
  return authState;
}

export const isSignedIn = derived(authState, value => value.isSignedIn);
export const isAdmin = derived(authState, value => value.roles.has('admin'));
export const currentUserId = derived(authState, value => value.userId);

export async function signOut() {
  if (clerkInstance) {
    await clerkInstance.signOut();
  }
}

export function getCachedClerk(): Clerk | null {
  return clerkInstance;
}

export function resetAuthForTesting() {
  removeListener?.();
  removeListener = null;
  clerkInstance = null;
  convexTokenFetcher = null;
  convexAuthSignedIn = null;
  authState.set({
    isLoaded: false,
    isSignedIn: false,
    userId: null,
    email: null,
    roles: new Set()
  });
}
